import React, { useState, useEffect } from "react";
import {
  Card,
  InputNumber,
  Button,
  message,
  Tabs,
  Typography,
  Row,
  Col,
  Table,
  Select,
  Image
} from "antd";
import { SaveOutlined, CalculatorOutlined } from "@ant-design/icons";
import axios from "axios";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const SingleServerModel = ({
  fileId,
  fileName,
  setSelectedFile,
  initialConfig,
}) => {
  const [activeTab, setActiveTab] = useState("1");
  const [loading, setLoading] = useState(false);

  // Store the full response from backend
  const [resultData, setResultData] = useState(null);

  const [inputData, setInputData] = useState({
    name: fileName || "Untitled Single-Server Analysis",
    arrival_rate: 20,
    service_rate: 500,
    option: "utilization",
    n: 3,
  });

  const OPTIONS = [
    { value: "utilization", label: "Utilization (ρ)" },
    { value: "L", label: "Average number in system (L)" },
    { value: "Lq", label: "Average number in queue (Lq)" },
    { value: "W", label: "Average time in system (W)" },
    { value: "Wq", label: "Average time in line (Wq)" },
    { value: "P0", label: "Probability system is empty (P0)" },
    { value: "Pn", label: "Probability of n customers (Pn)" },
  ];

  const handleLogout = () => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("username");
    window.location.href = "/login";
  };

  useEffect(() => {
    if (fileId) {
      fetchAnalysis();
    } else if (initialConfig) {
      setInputData((prev) => ({
        ...prev,
        name: initialConfig.name || prev.name,
      }));
    }
  }, [fileId]);

  const fetchAnalysis = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const url = `/api/retrieve_single_server_model/${fileId}/`;
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const { input_data, output_data, chart_url, name } = response.data;

      const restoredInput = {
        name: name || fileName,
        arrival_rate: input_data.arrival_rate,
        service_rate: input_data.service_rate,
        option: input_data.option,
        n: input_data.n || 0,
      };

      setInputData(restoredInput);
      setResultData({ output_data, chart_url });

    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to fetch analysis details");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSolve = async () => {
    if (
      inputData.arrival_rate === null ||
      inputData.service_rate === null ||
      (inputData.option === "Pn" && inputData.n === null)
    ) {
      message.error("Please fill in all required inputs correctly.");
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem("access_token");
      const payload = {
        name: inputData.name,
        input_data: {
          arrival_rate: inputData.arrival_rate,
          service_rate: inputData.service_rate,
          option: inputData.option,
        },
      };

      if (inputData.option === "Pn") {
        payload.input_data.n = inputData.n;
      }

      let response;
      if (fileId) {
        response = await axios.put(
          `/api/update_single_server_model/${fileId}/`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        response = await axios.post(
          "/api/save_single_server_model/",
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        // Ensure the frontend updates selectedFile.id after saving
        setSelectedFile({
          id: response.data.id,
          name: response.data.name,
          type: "single_server_model",
        });
      }

      setResultData({
        output_data: response.data.output_data,
        chart_url: response.data.chart_url,
      });

      message.success("Analysis saved and solved successfully");
      setActiveTab("2"); // Switch to results tab
    } catch (error) {
      console.error("Solve error:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        message.error("Failed to solve analysis");
      }
    } finally {
      setLoading(false);
    }
  };

  const renderOutputTable = () => {
    if (!resultData || !resultData.output_data) return null;

    const tableData = Object.entries(resultData.output_data).map(
      ([key, value], index) => ({
        key: index,
        metric: key,
        value: typeof value === "number" ? value.toFixed(4) : value,
      })
    );

    const columns = [
      { title: "Metric", dataIndex: "metric", key: "metric" },
      { title: "Value", dataIndex: "value", key: "value" },
    ];

    return (
      <Table
        dataSource={tableData}
        columns={columns}
        pagination={false}
        bordered
        size="small"
        style={{ marginTop: 20 }}
      />
    );
  };

  return (
    <Card
      title={<Title level={4}>{inputData.name}</Title>}
      extra={
        <Button
          type="primary"
          icon={<SaveOutlined />}
          onClick={handleSolve}
          loading={loading}
        >
          {fileId ? "Update & Solve" : "Save & Solve"}
        </Button>
      }
    >
      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="Inputs" key="1">
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <Card title="Rates" size="small">
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Arrival Rate (λ):</Text>
                  <br />
                  <InputNumber
                    value={inputData.arrival_rate}
                    onChange={(val) =>
                      setInputData({ ...inputData, arrival_rate: val })
                    }
                    min={0.0001}
                    step={0.1}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
                <div>
                  <Text strong>Service Rate (μ):</Text>
                  <br />
                  <InputNumber
                    value={inputData.service_rate}
                    onChange={(val) =>
                      setInputData({ ...inputData, service_rate: val })
                    }
                    min={0.0001}
                    step={0.1}
                    style={{ width: "100%", marginTop: 8 }}
                  />
                </div>
              </Card>
            </Col>
            <Col span={12}>
              <Card title="Analysis Goal" size="small">
                <div style={{ marginBottom: 16 }}>
                  <Text strong>Option:</Text>
                  <br />
                  <Select
                    value={inputData.option}
                    onChange={(val) =>
                      setInputData({ ...inputData, option: val })
                    }
                    style={{ width: "100%", marginTop: 8 }}
                    options={OPTIONS}
                  />
                </div>
                {inputData.option === "Pn" && (
                  <div>
                    <Text strong>Number of Customers (n):</Text>
                    <br />
                    <InputNumber
                      value={inputData.n}
                      onChange={(val) =>
                        setInputData({ ...inputData, n: val })
                      }
                      min={0}
                      precision={0}
                      style={{ width: "100%", marginTop: 8 }}
                    />
                  </div>
                )}
              </Card>
            </Col>
          </Row>
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <Button
              type="primary"
              size="large"
              icon={<CalculatorOutlined />}
              onClick={handleSolve}
              loading={loading}
            >
              Calculate
            </Button>
          </div>
        </TabPane>

        <TabPane tab="Results" key="2" disabled={!resultData}>
          <Row gutter={[24, 24]}>
            <Col span={24}>
              <Card title="Calculation Output">
                {renderOutputTable()}
              </Card>
            </Col>
            {resultData?.chart_url && (
              <Col span={24}>
                <Card title="Chart">
                  <div style={{ textAlign: "center" }}>
                    <Image
                      src={resultData.chart_url.startsWith('http') ? resultData.chart_url : `http://127.0.0.1:8000${resultData.chart_url}`}
                      alt="Single-Server Result Chart"
                      fallback="https://via.placeholder.com/600x400?text=Chart+Not+Available"
                      style={{ maxWidth: "100%", maxHeight: "500px" }}
                    />
                  </div>
                </Card>
              </Col>
            )}
          </Row>
        </TabPane>
      </Tabs>
    </Card>
  );
};

export default SingleServerModel;
